import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowUpRight, PlayCircle, Scissors } from 'lucide-react';
import { DOOR_STUDIOS, studioById, type StudioId } from '@/lib/studios';
import { QuickToolRow } from '@/components/quicktools/QuickToolCards';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

// THE FIRST SCREEN (Studio Blueprint 01/02): the question "what do you want
// to make?" with four labelled doors that answer it, each showing a real
// output and what its first step costs, then the free editor and the quick
// tools as one-line strips. No price wall, no carousel, nothing that is
// secretly a button.
//
// ?door=<studio> (or a utm_content naming one) puts that door first and lit;
// a visitor with work in progress gets a Continue line above the doors.

export interface ContinueItem {
  label: string;
  to: string;
}

/** "Continue: <last project>" for a returning visitor (blueprint 02). */
export function ContinueLink({ resume }: { resume: ContinueItem }) {
  const { t } = useTranslation();
  return (
    <div className="container mx-auto max-w-6xl px-4 pt-6 md:px-8 xl:px-4">
      <Link
        to={resume.to}
        data-testid="home-continue"
        className="flex items-center gap-3 rounded-2xl border border-primary/40 bg-primary/10 px-4 py-3 text-[15px] font-semibold text-foreground"
      >
        <PlayCircle aria-hidden className="h-5 w-5 shrink-0 text-primary" />
        <span className="min-w-0 flex-1 truncate">{t('home.continue', { label: resume.label })}</span>
        <ArrowRight aria-hidden className="h-4 w-4 shrink-0 text-primary" />
      </Link>
    </div>
  );
}

export function HomeDoors({ door, resume }: { door: StudioId | null; resume: ContinueItem | null }) {
  const { t, i18n } = useTranslation();
  const doors = useMemo(() => {
    const ids = [...DOOR_STUDIOS];
    if (door && ids.includes(door)) ids.sort((a, b) => (a === door ? -1 : b === door ? 1 : 0));
    return ids.map(studioById);
  }, [door]);

  return (
    <section
      aria-labelledby="home-title"
      data-testid="home-doors"
      className="relative isolate pb-8 pt-12 sm:pb-14 sm:pt-16"
    >
      {/* The blueprint's doors, added under the hero and the packs. */}
      <div className="container mx-auto max-w-6xl px-4 md:px-8 xl:px-4">
        <header>
          <p className="eyebrow">{t('home.eyebrow')}</p>
          <h2 id="home-title" className="mt-2 text-balance font-display text-[1.75rem] font-bold leading-[1.05] tracking-[-0.02em] sm:text-[2.6rem]">
            {t('home.title')}
          </h2>
          <p className="mt-2 max-w-2xl text-pretty text-[15px] leading-snug text-muted-foreground sm:mt-3 sm:text-[1.1rem]">{t('home.lead')}</p>
        </header>

        {resume && (
          <Link
            to={resume.to}
            data-testid="home-continue"
            className="mt-4 flex items-center gap-3 rounded-2xl border border-primary/40 bg-primary/10 px-4 py-3 text-[15px] font-semibold text-foreground"
          >
            <PlayCircle aria-hidden className="h-5 w-5 shrink-0 text-primary" />
            <span className="min-w-0 flex-1 truncate">{t('home.continue', { label: resume.label })}</span>
            <ArrowRight aria-hidden className="h-4 w-4 shrink-0 text-primary" />
          </Link>
        )}

        <ul className="mt-4 grid grid-cols-2 gap-2.5 sm:mt-8 sm:gap-4 lg:grid-cols-4">
          {doors.map((s) => {
            const lit = s.id === door;
            return (
              <li key={s.id}>
                <Link
                  to={s.path(i18n.language)}
                  data-testid={`door-${s.id}`}
                  onClick={() => trackEvent('home_door_clicked', { studio: s.id, lit })}
                  className={cn(
                    'group relative flex h-full flex-col overflow-hidden rounded-2xl border bg-white/[0.03] transition-[transform,border-color] duration-300 [@media(hover:hover)]:hover:-translate-y-0.5',
                    lit ? 'border-primary/70 shadow-[0_0_32px_-8px_hsl(var(--primary)/0.6)]' : 'border-white/10 hover:border-primary/40',
                  )}
                >
                  <span className="relative block aspect-[4/3] overflow-hidden bg-black sm:aspect-[4/5]">
                    <img
                      src={s.poster}
                      alt=""
                      width={400}
                      height={500}
                      decoding="async"
                      className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                    <span aria-hidden className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent" />
                    <span
                      className={cn(
                        'absolute bottom-2 left-2 rounded-full px-2 py-0.5 text-[11.5px] font-semibold ring-1 ring-inset backdrop-blur-sm sm:bottom-3 sm:left-3 sm:px-2.5 sm:text-[12.5px]',
                        s.free ? 'bg-black/50 text-accent ring-accent/40' : 'bg-black/50 text-primary ring-primary/40',
                      )}
                    >
                      {t(`studios.${s.key}.entry`)}
                    </span>
                  </span>
                  <span className="flex flex-1 items-start justify-between gap-2 p-3 sm:p-4">
                    <span className="font-display text-[14.5px] font-semibold leading-snug text-foreground sm:text-[1.05rem]">
                      {t(`studios.${s.key}.door`)}
                    </span>
                    <ArrowUpRight aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-primary transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="mt-2.5 sm:mt-4">
          <Link
            to={studioById('editor').path(i18n.language)}
            data-testid="home-strip-editor"
            className="group flex min-h-[52px] items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-2.5 transition-colors hover:border-accent/50"
          >
            <Scissors aria-hidden className="h-5 w-5 shrink-0 text-accent" />
            <span className="flex-1 text-[14.5px] text-muted-foreground">
              {t('home.editorStrip')} <span className="whitespace-nowrap font-semibold text-foreground">{t('home.editorStripCta')}</span>
            </span>
            <span className="rounded-full bg-accent/10 px-2.5 py-0.5 text-[12px] font-semibold text-accent ring-1 ring-inset ring-accent/30">{t('studios.editor.entry')}</span>
          </Link>
        </div>

        {/* The five quick tools, one row (owner 2026-10-16), under the editor strip. */}
        <div className="mt-3 sm:mt-5">
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <p className="text-[13px] font-semibold uppercase tracking-[0.16em] text-primary">{t('home.quickRowTitle')}</p>
            <Link to="/tools" className="text-[13px] font-semibold text-muted-foreground hover:text-foreground">{t('home.quickRowAll')} →</Link>
          </div>
          <QuickToolRow />
        </div>
      </div>
    </section>
  );
}
